import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import Pagination from '../components/Pagination';

/**
 * Purged archives — the `deleted_archives` tombstones.
 *
 * Deliberately has no actions. Every archive on this page has already been
 * destroyed: there is no row to pause, resume, re-crawl or delete, and no
 * snapshots to browse. It exists to answer "what did we delete, and how big
 * was it?" — which before the tombstone table could only be answered by
 * grepping per-job log files that record an archive id and nothing that
 * identifies it.
 */

type PurgedRow = {
  archive_id: number;
  domain: string;
  path_prefix: string | null;
  seed_url: string | null;
  site_type: string | null;
  status_at_purge: string;
  public: boolean;
  pages_fetched: number;
  bytes_fetched: number;
  errors_count: number;
  snapshots_count: number;
  snapshot_text_count: number;
  crawl_queue_count: number;
  r2_objects_deleted: number | null;
  archive_created_at: string | null;
  last_crawled_at: string | null;
  purge_started_at: string | null;
  purge_completed_at: string | null;
  created_by: number | null;
  purged_by: number | null;
  note: string | null;
};

type ListResponse = {
  data: PurgedRow[];
  meta: {
    total: number;
    limit: number;
    offset: number;
    snapshots_total: number;
    bytes_fetched_total: number;
    r2_objects_deleted_total: number;
  };
};

const PAGE_SIZE = 50;

function formatBytes(bytes: number): string {
  if (bytes >= 1099511627776) return `${(bytes / 1099511627776).toFixed(1)} TB`;
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Backfilled rows that predate the tombstone table have no recoverable
 *  domain — the purge logs only ever wrote the archive id. */
function isUnnamed(r: PurgedRow): boolean {
  return r.domain === '(unknown)';
}

/**
 * Lifetime counters (pages/bytes fetched, created_at) live only on the
 * `archives` row, so a tombstone reconstructed from logs after that row was
 * already deleted has no way to recover them — they come back as 0/null
 * meaning "unknown", not "none". Rendering that as "0 B" would assert
 * something false about an archive that fetched hundreds of thousands of
 * pages, so unknown reads as an em dash instead.
 */
function isBackfilled(r: PurgedRow): boolean {
  return (r.note ?? '').startsWith('backfilled from');
}

export default function Purged() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = params.get('q') ?? '';
  const incomplete = params.get('incomplete') === '1';
  const page = Math.max(1, Number(params.get('page') ?? '1') | 0);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api<ListResponse>('/archives/purged', {
      query: {
        q: q || undefined,
        incomplete: incomplete ? '1' : undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      },
    })
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [q, incomplete, page]);

  useEffect(() => { load(); }, [load]);

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');   // a narrower filter invalidates the page number
    setParams(next);
  }

  function goToPage(p: number) {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next);
  }

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const incompleteRows = rows.filter((r) => r.purge_completed_at === null);

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Purged archives</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Archives that have been deleted, and what they held while they were
          alive. Nothing here can be acted on — the archive, its snapshots, its
          search index and its R2 objects are all gone. Restoring one means
          re-crawling it, or reloading it from an offline copy.
        </p>

        {data && (
          <p className="muted" style={{ fontSize: 13 }}>
            <strong>{total.toLocaleString()}</strong> purged
            {' · '}{data.meta.snapshots_total.toLocaleString()} snapshots
            {' · '}{data.meta.r2_objects_deleted_total.toLocaleString()} R2 objects removed
            {' · '}{formatBytes(data.meta.bytes_fetched_total)} fetched
            {' '}<span title="Lifetime byte counts exist only for archives purged after tombstones were introduced; rows rebuilt from logs contribute 0 to this total">(partial)</span>
          </p>
        )}

        <p className="muted" style={{ fontSize: 12 }}>
          Rows marked with an em dash were rebuilt from purge logs after the
          archive row was already gone. The logs record what the purge removed,
          but never the archive's lifetime counters or its domain — so those
          columns are unrecoverable, not zero.
        </p>

        {incompleteRows.length > 0 && !incomplete && (
          <p className="muted" style={{ fontSize: 13 }}>
            {incompleteRows.length} purge(s) on this page never completed — the
            archive row is gone but child rows and R2 objects may still be
            orphaned. Re-running <code>purge-archive --archive {'{id}'}</code> is
            idempotent and finishes the job.
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <input
            type="search"
            placeholder="Filter by domain…"
            defaultValue={q}
            onKeyDown={(e) => {
              if (e.key === 'Enter') updateFilter('q', (e.target as HTMLInputElement).value);
            }}
            style={{ flex: '1 1 240px', minWidth: 0 }}
          />
          <button
            className="secondary"
            onClick={() => updateFilter('incomplete', incomplete ? '' : '1')}
            style={{ fontWeight: incomplete ? 700 : 400 }}
            title="Show only purges that did not run to completion"
          >
            {incomplete ? 'Showing incomplete only' : 'Incomplete only'}
          </button>
        </div>

        {error && <p className="error">{error}</p>}

        {loading && !data ? (
          <p className="muted" style={{ marginTop: 12 }}>Loading…</p>
        ) : rows.length === 0 ? (
          <p className="muted" style={{ marginTop: 12 }}>
            {incomplete ? 'No incomplete purges — every purge finished cleanly.' : 'No purged archives.'}
          </p>
        ) : (
          <>
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Domain</th>
                    <th>Died as</th>
                    <th style={{ textAlign: 'right' }}>Snapshots</th>
                    <th style={{ textAlign: 'right' }}>Indexed</th>
                    <th style={{ textAlign: 'right' }}>Queue</th>
                    <th style={{ textAlign: 'right' }}>R2 removed</th>
                    <th style={{ textAlign: 'right' }}>Fetched</th>
                    <th>Created</th>
                    <th>Purged</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.archive_id}>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{r.archive_id}</td>
                      <td title={r.seed_url ?? undefined}>
                        {isUnnamed(r)
                          ? <span className="muted" title="Purged before tombstones existed — the logs recorded only the id">(unknown)</span>
                          : r.domain}
                        {r.site_type && <span className="muted"> · {r.site_type}</span>}
                      </td>
                      <td>
                        <span className={`status-pill ${r.status_at_purge}`}>{r.status_at_purge}</span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{r.snapshots_count.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{r.snapshot_text_count.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{r.crawl_queue_count.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.r2_objects_deleted === null
                          ? <span className="muted">—</span>
                          : r.r2_objects_deleted.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.bytes_fetched === 0 && isBackfilled(r)
                          ? <span className="muted" title="Not recoverable — this tombstone was rebuilt from logs after the archive row was already deleted">—</span>
                          : formatBytes(r.bytes_fetched)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }} className="muted">{formatDate(r.archive_created_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {r.purge_completed_at === null ? (
                          <span
                            className="status-pill error"
                            title={r.note ?? 'Purge did not complete'}
                          >
                            incomplete
                          </span>
                        ) : (
                          <span className="muted">{formatDate(r.purge_completed_at)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              lastPage={lastPage}
              total={total}
              perPage={PAGE_SIZE}
              onChange={goToPage}
            />
          </>
        )}
      </div>
    </>
  );
}
